import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/store/hooks';
import { formatErrorMessage } from '@/utils/helpers';
import '@/styles/modal.scss';


interface DeleteItem {
  id: string;
  name: string;
}

interface DeleteConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (items: DeleteItem[], userId: string) => Promise<void>;
  onSuccess: (count: number) => void;
  title: string;
  items: DeleteItem[];
  itemType: string; // e.g., 'turnaround', 'device', 'location'
  isLoading?: boolean;
  error?: string | null;
  showList?: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  onSuccess,
  title,
  items,
  itemType,
  isLoading = false,
  error = null,
  showList = true
}) => {
  const { t } = useTranslation();
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);
  const { data: userData } = useAppSelector((state) => state.user);

  const handleConfirm = async () => {
    if (!userData?.id || items.length === 0) return;

    setInternalLoading(true);
    setInternalError(null);

    try {
      await onConfirm(items, userData.id);
      onSuccess(items.length);
    } catch (err) {
      const errorMessage = formatErrorMessage(err, `Failed to delete ${itemType}. Please try again.`);
      setInternalError(errorMessage);
      setInternalLoading(false);
    }
  };

  const handleClose = () => {
    if (!internalLoading && !isLoading) {
      setInternalError(null);
      onClose();
    }
  };

  const displayError = error || internalError;
  const displayLoading = isLoading || internalLoading;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          className: 'modal-dialog'
        }
      }}
    >
      <Box className="modal-header">
        <DialogTitle className="modal-header-title">
          {title}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          size="small"
          disabled={displayLoading}
          className="modal-close-button"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent className="modal-content-padded">
        <Box>
          <Typography className="modal-main-content">
            {items.length > 1
              ? t(
                  "deleteConfirmMultiple",
                  "Are you sure you want to {{action}} these {{count}} {{itemType}}s?",
                  {
                    action: t(
                      itemType === "device"
                        ? "deleteActionRemove"
                        : "deleteActionDelete",
                      itemType === "device" ? "remove" : "delete"
                    ),
                    count: items.length,
                    itemType: t(itemType, itemType),
                  }
                )
              : t(
                  "deleteConfirmSingle",
                  "Are you sure you want to {{action}} this {{itemType}}?",
                  {
                    action: t(
                      itemType === "device"
                        ? "deleteActionRemove"
                        : "deleteActionDelete",
                      itemType === "device" ? "remove" : "delete"
                    ),
                    itemType: t(itemType, itemType),
                  }
                )}
          </Typography>

          {items.length > 0 && showList && (
            <Box
              component="ul"
              className="modal-list"
            >
              {items.map((item, index) => (
                <li key={item.id || index}>{item.name}</li>
              ))}
            </Box>
          )}

          <Typography className="modal-label modal-warning-text modal-mt-2">
            {t('deleteDocumentationWarning', 'This action cannot be undone.')}
          </Typography>
        </Box>

        {displayError && (
          <Alert severity="error" className="modal-alert-error">
            {displayError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions className="modal-actions-flex">
        <Button
          variant="outlined"
          onClick={handleClose}
          fullWidth
          disabled={displayLoading}
          className="modal-button-base modal-button-outlined"
        >
          {t('cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          fullWidth
          disabled={displayLoading}
          loading={displayLoading}
          className="modal-button-base modal-button-primary"
        >
          {displayLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : itemType === "device" ? (
            t("remove", "Remove")
          ) : (
            t("delete", "Delete")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationModal;